import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RefreshCw, Loader2, ChevronDown, ChevronUp, KeyRound,
  CalendarX2, Tv, Euro, CheckCircle2, AlertTriangle, XCircle, Server,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usersService, UserDiagnostics, getErrorMessage } from '@/services';
import dayjs from 'dayjs';

export default function Diagnostics() {
  const { t } = useTranslation();
  const [data, setData] = useState<UserDiagnostics[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const fetchData = async () => {
    setIsLoading(true);
    try {
      setData(await usersService.getDiagnostics());
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggle = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const levelBadge = (level: UserDiagnostics['level']) => {
    if (level === 'error') return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-error-50 text-error-600 border border-error-100">
        <XCircle size={13} /> {t('diagnostics.levelError')}
      </span>
    );
    if (level === 'warning') return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-warning-50 text-warning-600 border border-warning-100">
        <AlertTriangle size={13} /> {t('diagnostics.levelWarning')}
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-success-50 text-success-600 border border-success-100">
        <CheckCircle2 size={13} /> {t('diagnostics.levelOk')}
      </span>
    );
  };

  const daznChip = (u: UserDiagnostics) => {
    const s = u.dazn.status;
    const cls = s === 'ok'
      ? 'bg-success-50 text-success-600 border-success-100'
      : 'bg-error-50 text-error-600 border-error-100';
    const label = s === 'ok'
      ? t('diagnostics.tokenOk')
      : s === 'expired' ? t('diagnostics.tokenExpired') : t('diagnostics.tokenMissing');
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>
        <KeyRound size={12} /> {label}
      </span>
    );
  };

  const sevCls = (sev: string) =>
    sev === 'error' ? 'text-error-600'
    : sev === 'warning' ? 'text-warning-600'
    : 'text-dark-400';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('diagnostics.title')}</h1>
          <p className="text-dark-400 mt-1">{t('diagnostics.subtitle')}</p>
        </div>
        <button onClick={fetchData} disabled={isLoading} className="btn-secondary">
          {isLoading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
          {t('common.refresh')}
        </button>
      </div>

      {isLoading && data.length === 0 ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-dark-800 rounded" />)}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {data.map(u => {
            const isOpen = expanded.has(u.userId);
            const hasDetail = u.events.failures.length > 0 || u.linear.errors.length > 0;
            return (
              <div key={u.userId} className="card !p-0 overflow-hidden">
                {/* Riga sintesi */}
                <button
                  onClick={() => hasDetail && toggle(u.userId)}
                  className={`w-full flex flex-wrap items-center gap-3 px-5 py-4 text-left ${hasDetail ? 'hover:bg-dark-800 cursor-pointer' : 'cursor-default'} transition-colors`}
                >
                  <span className="font-semibold text-dark-100 w-32 truncate">{u.username}</span>
                  {levelBadge(u.level)}
                  {daznChip(u)}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-dark-800 text-dark-300 border-dark-700">
                    <CalendarX2 size={12} />
                    {t('diagnostics.eventsSummary', { failed: u.events.failedCount, total: u.events.total48h })}
                  </span>
                  {u.linear.total > 0 && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${u.linear.errors.length > 0 ? 'bg-error-50 text-error-600 border-error-100' : 'bg-dark-800 text-dark-300 border-dark-700'}`}>
                      <Tv size={12} />
                      {u.linear.errors.length > 0
                        ? t('diagnostics.linearErrors', { count: u.linear.errors.length })
                        : t('diagnostics.linearOk', { count: u.linear.total })}
                    </span>
                  )}
                  {u.identity && u.identity.androidtvChannels > 0 && (
                    <span title={t('diagnostics.identityTitle')} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-dark-800 text-dark-300 border-dark-700">
                      <Tv size={12} /> {t('diagnostics.identityAndroidTv', { count: u.identity.androidtvChannels })}
                    </span>
                  )}
                  {u.identity?.rejectedSince && (
                    <span title={t('diagnostics.identityTitle')} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-warning-50 text-warning-600 border-warning-100">
                      <Tv size={12} /> {t('diagnostics.identityRejected', { since: new Date(u.identity.rejectedSince).toLocaleString() })}
                    </span>
                  )}
                  {u.identity && u.identity.uaMismatches.length > 0 && (
                    <span title={u.identity.uaMismatches.join(', ')} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-warning-50 text-warning-600 border-warning-100">
                      <Tv size={12} /> {t('diagnostics.identityMismatch', { count: u.identity.uaMismatches.length })}
                    </span>
                  )}
                  {u.identity?.rolloutOffWithRows && (
                    <span title={t('diagnostics.identityRolloutOffHint')} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-error-50 text-error-600 border-error-100">
                      <Tv size={12} /> {t('diagnostics.identityRolloutOff', { rows: u.identity.rolloutOffWithRows.rows, channels: u.identity.rolloutOffWithRows.channels })}
                    </span>
                  )}
                  {u.identity && u.identity.droppingLegs.length > 0 && (
                    <span title={u.identity.droppingLegs.join(', ')} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-warning-50 text-warning-600 border-warning-100">
                      <Tv size={12} /> {t('diagnostics.identityDropping', { count: u.identity.droppingLegs.length })}
                    </span>
                  )}
                  {u.serversCircuitOpen && u.serversCircuitOpen.length > 0 && (
                    <span
                      title={`${t('diagnostics.serversCircuitOpenTitle')}: ${u.serversCircuitOpen.map((c) => `${c.name} (${c.reason}, ${new Date(c.since).toLocaleString()})`).join(', ')}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-warning-50 text-warning-600 border-warning-100"
                    >
                      <Server size={12} /> {t('diagnostics.serversCircuitOpen', { count: u.serversCircuitOpen.length })}
                    </span>
                  )}
                  {u.linear.heldLegs && u.linear.heldLegs.length > 0 && (
                    <span
                      title={`${t('diagnostics.heldLegsTitle')}: ${u.linear.heldLegs.map((c) => `${c.channel} (${c.legs.map((l) => `${l.name} ${l.reason === 'user-stop' ? t('diagnostics.heldUserStop') : t('diagnostics.heldUnwanted')}`).join(', ')})`).join('; ')}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-dark-800 text-dark-300 border-dark-700"
                    >
                      <Tv size={12} /> {t('diagnostics.heldLegs', { count: u.linear.heldLegs.length })}
                    </span>
                  )}
                  {(u.paywallUpcoming.ppv > 0 || u.paywallUpcoming.extra > 0) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-warning-50 text-warning-600 border-warning-100">
                      <Euro size={12} />
                      {t('diagnostics.paywallUpcoming', { count: u.paywallUpcoming.ppv + u.paywallUpcoming.extra })}
                    </span>
                  )}
                  <span className="ml-auto text-dark-500">
                    {hasDetail && (isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                  </span>
                </button>

                {/* Dettaglio */}
                {isOpen && hasDetail && (
                  <div className="border-t border-dark-700 px-5 py-4 space-y-4 bg-dark-900">
                    {u.events.failures.map((f, i) => (
                      <div key={i}>
                        <p className={`text-sm font-semibold ${sevCls(f.severity)}`}>
                          {f.label} · {f.count}
                        </p>
                        <p className="text-xs text-dark-400 mt-0.5">→ {f.action}</p>
                        <div className="mt-2 space-y-1">
                          {f.samples.map((s, j) => (
                            <p key={j} className="text-xs text-dark-500 font-mono truncate" title={s.message}>
                              {dayjs(s.time).format('DD/MM HH:mm')} · {s.title} — {s.message || '-'}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                    {u.linear.errors.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-error-600">{t('diagnostics.linearErrorsTitle')}</p>
                        <p className="text-xs text-dark-500 font-mono mt-1">{u.linear.errors.join(', ')}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {data.length === 0 && !isLoading && (
            <div className="card"><p className="text-dark-400">{t('common.noData')}</p></div>
          )}
        </div>
      )}
    </div>
  );
}
